import React, { useState, useEffect } from 'react';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';

// Organized component imports
import { Login, Home, StrategyModule } from './components/Layout';
import { PermissionGuard, ErrorBoundary } from './components/common';
import { TeamManagement } from './components/TeamSettings';
import { ReviewDashboard } from './components/ContentReview';
import { VideoEditorModule } from './components/VideoEditor';
import { SpecWriter } from './components/SpecWriter';

import { usePermissions } from './hooks/usePermissions';
import axios from 'axios';
import './App.css';

// Initialize MSAL
const msalInstance = new PublicClientApplication(msalConfig);

interface TeamMember {
  name: string;
  email: string;
  capacity: number;
  role: 'admin' | 'user';
  permissions: {
    modules: string[];
    subcategories: string[];
  };
  showInWorkload?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  name: string;
  email: string;
  account: any;
  isExternal?: boolean;
  token?: string;
}

type Tab = 'home' | 'team-settings' | 'content-review' | 'spec-writer' | 'strategy' | 'video-editor';

function AppContent({ user, setUser }: { user: User | null; setUser: (user: User | null) => void }) {
  const { instance } = useMsal();
  const { hasModuleAccess, refetch: refetchPermissions } = usePermissions(user);
  const [team, setTeam] = useState<TeamMember[]>([]);
  // Deep link (2026-08-21): mention emails point at ?tab=content-review&cycle=<id>.
  // Only the tab is resolved here; ReviewDashboard reads `cycle` itself.
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return t === 'content-review' ? 'content-review' : 'home';
    } catch { return 'home'; }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Change password modal state (for external users)
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Team-load tracking
  const [teamLoaded, setTeamLoaded] = useState(false);

  // Helper function to get auth headers for API calls
  const getAuthHeaders = async () => {
    if (!user) throw new Error('User not authenticated');

    if (user.isExternal) {
      // External user - use JWT token
      const token = user.token || sessionStorage.getItem('externalUserToken');
      return {
        'Authorization': `Bearer ${token}`,
        'x-user-email': user.email,
        'x-user-name': user.name,
        'x-external-user': 'true'
      };
    } else {
      // Internal user - use MSAL token.
      // Request SharePoint scopes up front so endpoints that forward the
      // token to Microsoft Graph (Content Review check-files, diagnose,
      // document parsing) work without a second token acquisition. Falls
      // back to interactive popup on first login when consent is needed.
      const sharePointScopes = ['User.Read', 'Sites.ReadWrite.All', 'Files.ReadWrite.All'];
      try {
        const response = await instance.acquireTokenSilent({
          scopes: sharePointScopes,
          account: user.account
        });
        return {
          'Authorization': `Bearer ${response.accessToken}`,
          'x-user-email': user.email,
          'x-user-name': user.name
        };
      } catch (error: any) {
        if (error.name === 'InteractionRequiredAuthError') {
          const response = await instance.acquireTokenPopup({
            scopes: sharePointScopes,
            account: user.account
          });
          return {
            'Authorization': `Bearer ${response.accessToken}`,
            'x-user-email': user.email,
            'x-user-name': user.name
          };
        }
        throw error;
      }
    }
  };

  // Fetch team data independently on user login (kept so the header/user
  // menu + downstream views that expect a populated team list still work).
  useEffect(() => {
    if (!user || teamLoaded) return;

    const fetchTeamData = async () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/team`, { headers });
        setTeam(res.data);
        setTeamLoaded(true);
      } catch (err: any) {
        console.error('Error fetching team data:', err);
        // Don't block app if team fetch fails
        setTeamLoaded(true);
      }
    };

    fetchTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, teamLoaded]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]);

  // Handle logout
  const handleLogout = async () => {
    try {
      // Clear all local state
      setUser(null);
      setTeam([]);
      setTeamLoaded(false);
      setTab('home');

      // Clear external user token if present
      sessionStorage.removeItem('externalUserToken');

      // Logout from MSAL only if internal user
      if (user && !user.isExternal && user.account) {
        await instance.logout({
          account: user.account,
          postLogoutRedirectUri: window.location.origin
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even if MSAL logout fails, clear local state
      setUser(null);
    }
  };

  // Handle logout with confirmation
  const handleLogoutWithConfirmation = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      handleLogout();
    }
  };

  // Handle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileNavClick = (newTab: Tab) => {
    setTab(newTab);
    closeMobileMenu();
  };

  // Handle change password for external users
  const handleChangePassword = async () => {
    if (!user?.isExternal) return;

    setPasswordError(null);
    setPasswordSuccess(false);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

    try {
      const headers = await getAuthHeaders();
      await axios.post(`${backendUrl}/api/auth/change-password`,
        { currentPassword, newPassword },
        { headers }
      );

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Close modal after 2 seconds on success
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (err: any) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Reset change password modal state
  const closeChangePasswordModal = () => {
    setShowChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(false);
  };

  return (
    <div className="App">
      {user && (
        <header style={{
          background: '#FFFFFF',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div className="header-container" style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '70px'
            }}>
              {/* Left side - Logo/Brand */}
              <div
                className="header-brand"
                onClick={() => setTab('home')}
                role="button"
                tabIndex={0}
                title="Go to home"
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTab('home'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  minWidth: 0,
                  padding: '4px 8px',
                  marginLeft: '-8px',
                  borderRadius: '8px',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#F4F6F8'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <div>
                  <h1 style={{
                    color: '#123250',
                    fontSize: '26px',
                    fontWeight: '650',
                    margin: 0,
                    letterSpacing: '-0.02em',
                    fontFamily: 'Barlow, sans-serif',
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap'
                  }}>
                    Cellco Hub
                  </h1>
                  <p style={{
                    color: '#666666',
                    fontSize: '13px',
                    fontWeight: '500',
                    margin: 0,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    fontFamily: 'Inter, sans-serif',
                    whiteSpace: 'nowrap'
                  }}>
                    Marketing Operations
                  </p>
                </div>
              </div>

              {/* Center - Navigation. Walk station 1 (Fredrik, 2026-08-13):
                  main nav carries the content-pipeline modules in FLOW order
                  (spec -> produce -> review -> strategy reference); Team
                  Settings moved to the gear button in the user section.
                  Future Planning module slots in at the front of this list. */}
              <nav className="header-nav" style={{
                display: 'flex',
                gap: '8px',
                background: '#F8F9FA',
                padding: '6px',
                borderRadius: '12px'
              }}>
                {([
                  // Video Editor has no module permission yet — the backend
                  // enforces via the video_editor subcategory (403 if absent).
                  { tab: 'spec-writer' as Tab, label: 'Spec Writer', show: hasModuleAccess('specWriter') },
                  { tab: 'video-editor' as Tab, label: 'Video Editor', show: true },
                  { tab: 'content-review' as Tab, label: 'Content Review', show: hasModuleAccess('contentReview') },
                  { tab: 'strategy' as Tab, label: 'Strategy', show: hasModuleAccess('strategy') }
                ]).filter(item => item.show).map(item => (
                  <button
                    key={item.tab}
                    className="nav-button"
                    onClick={() => setTab(item.tab)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: tab === item.tab ? '#123250' : 'transparent',
                      color: tab === item.tab ? '#FFFFFF' : '#161616',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      fontSize: '15px',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      fontFamily: 'Inter, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (tab !== item.tab) {
                        (e.target as HTMLElement).style.backgroundColor = '#E1EBF5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (tab !== item.tab) {
                        (e.target as HTMLElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              {/* Right side - User info and actions */}
              <div className="user-section" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                {/* Team Settings gear (walk station 1) — admin/config lives
                    outside the flow tabs. Direct-navigate; hidden without
                    the teamSettings module permission. */}
                {hasModuleAccess('teamSettings') && (
                  <button
                    className="nav-gear-button"
                    onClick={() => setTab('team-settings')}
                    title="Team Settings"
                    aria-label="Team Settings"
                    style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: tab === 'team-settings' ? '#123250' : 'transparent',
                      border: '1px solid ' + (tab === 'team-settings' ? '#123250' : '#E0E0E0'),
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      lineHeight: 1,
                      transition: 'all 0.2s ease',
                      filter: tab === 'team-settings' ? 'invert(1)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (tab !== 'team-settings') {
                        (e.currentTarget as HTMLElement).style.backgroundColor = '#E1EBF5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (tab !== 'team-settings') {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    ⚙️
                  </button>
                )}

                {/* Hamburger Menu Button (Mobile Only) */}
                <button
                  className={`hamburger-button ${mobileMenuOpen ? 'open' : ''}`}
                  onClick={toggleMobileMenu}
                  style={{ display: 'none' }}
                >
                  <div className="hamburger-line"></div>
                  <div className="hamburger-line"></div>
                  <div className="hamburger-line"></div>
                </button>

                <div className="user-info" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 16px',
                  background: '#F8F9FA',
                  borderRadius: '12px',
                  border: '1px solid #E0E0E0',
                  minWidth: 0,
                  maxWidth: '340px'
                }}>
                  <div className="user-avatar" style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#123250',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    flexShrink: 0
                  }}>
                    👤
                  </div>
                  <span
                    className="user-name"
                    title={user.name + (user.isExternal ? ' (External)' : '')}
                    style={{
                      color: '#161616',
                      fontWeight: '500',
                      fontSize: '15px',
                      fontFamily: 'Inter, sans-serif',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: '1 1 auto'
                    }}
                  >
                    {user.name}
                    {user.isExternal && (
                      <span className="user-external-tag" style={{ fontSize: '11px', color: '#666', marginLeft: '6px' }}>(External)</span>
                    )}
                  </span>
                  {user.isExternal && (
                    <button
                      className="change-password-btn"
                      onClick={() => setShowChangePassword(true)}
                      style={{
                        backgroundColor: '#FFFFFF',
                        color: '#4c5eba',
                        border: '1px solid #4c5eba',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, sans-serif',
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLElement).style.backgroundColor = '#4c5eba';
                        (e.target as HTMLElement).style.color = '#FFFFFF';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLElement).style.backgroundColor = '#FFFFFF';
                        (e.target as HTMLElement).style.color = '#4c5eba';
                      }}
                    >
                      Change Password
                    </button>
                  )}
                  <button
                    className="signout-btn"
                    onClick={handleLogoutWithConfirmation}
                    style={{
                      backgroundColor: '#FFFFFF',
                      color: '#161616',
                      border: '1px solid #C6C6C6',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Inter, sans-serif',
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = '#F4F4F4';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = '#FFFFFF';
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Change Password Modal for External Users */}
      {showChangePassword && user?.isExternal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            width: '400px',
            maxWidth: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{ marginBottom: '20px', color: '#123250', fontSize: '20px' }}>Change Password</h2>

            {passwordSuccess ? (
              <div style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '15px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                Password changed successfully!
              </div>
            ) : (
              <>
                {passwordError && (
                  <div style={{
                    backgroundColor: '#ffebee',
                    color: '#c62828',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    {passwordError}
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeChangePasswordModal}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: changingPassword ? '#9ca3af' : '#4c5eba',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: changingPassword ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay and Sidebar */}
      {user && (
        <>
          <div
            className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
            onClick={closeMobileMenu}
          ></div>

          <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <h3 style={{
              color: '#123250',
              fontSize: '20px',
              fontWeight: '650',
              marginBottom: '32px',
              textAlign: 'center',
              fontFamily: 'Barlow, sans-serif'
            }}>
              Cellco Hub
            </h3>

            {/* Flow order mirrors the desktop nav (walk station 1);
                Team Settings sits last with the gear, matching its
                out-of-flow placement on desktop. */}
            {hasModuleAccess('specWriter') && (
              <button
                className={`mobile-menu-item ${tab === 'spec-writer' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('spec-writer')}
              >
                Spec Writer
              </button>
            )}

            <button
              className={`mobile-menu-item ${tab === 'video-editor' ? 'active' : ''}`}
              onClick={() => handleMobileNavClick('video-editor')}
            >
              Video Editor
            </button>

            {hasModuleAccess('contentReview') && (
              <button
                className={`mobile-menu-item ${tab === 'content-review' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('content-review')}
              >
                Content Review
              </button>
            )}

            {hasModuleAccess('strategy') && (
              <button
                className={`mobile-menu-item ${tab === 'strategy' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('strategy')}
              >
                Strategy
              </button>
            )}

            {hasModuleAccess('teamSettings') && (
              <button
                className={`mobile-menu-item ${tab === 'team-settings' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('team-settings')}
              >
                ⚙️ Team Settings
              </button>
            )}

            <div style={{
              position: 'absolute',
              bottom: '24px',
              left: '24px',
              right: '24px',
              padding: '16px',
              background: '#F8F9FA',
              borderRadius: '12px',
              textAlign: 'center',
              border: '1px solid #E0E0E0'
            }}>
              <p style={{
                color: '#666666',
                fontSize: '14px',
                margin: '0 0 12px 0',
                fontFamily: 'Inter, sans-serif'
              }}>
                Signed in as
              </p>
              <p style={{
                color: '#123250',
                fontSize: '16px',
                fontWeight: '650',
                margin: '0 0 16px 0',
                fontFamily: 'Barlow, sans-serif'
              }}>
                {user.name}
              </p>
              <button
                onClick={() => {
                  closeMobileMenu();
                  handleLogoutWithConfirmation();
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#161616',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Inter, sans-serif'
                }}
                onMouseDown={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#393939';
                }}
                onMouseUp={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#161616';
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      <main className="main-content" style={{
        maxWidth: tab === 'strategy' ? 'none' : '1400px',
        margin: '0 auto',
        padding: tab === 'strategy' ? '0' : '32px 24px',
        minHeight: 'calc(100vh - 70px)',
        background: tab === 'strategy' ? '#F4F4F4' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        zIndex: 1
      }}>

        {/* Home Page - Always available, no data loading required */}
        {tab === 'home' && (
          <ErrorBoundary moduleName="Home" resetKey={tab}>
            <Home
              userName={user?.name || 'User'}
              onNavigate={setTab}
            />
          </ErrorBoundary>
        )}

        {/* Team Settings Tab */}
        {tab === 'team-settings' && (
          <ErrorBoundary moduleName="Team Settings" resetKey={tab}>
          <PermissionGuard user={user} requireModule="teamSettings">
            <div style={{
              padding: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <TeamManagement team={team} setTeam={setTeam} user={user} onOwnPermissionsMaybeChanged={refetchPermissions} />
            </div>
          </PermissionGuard>
          </ErrorBoundary>
        )}

        {/* Content Review Tab */}
        {tab === 'content-review' && (
          <ErrorBoundary moduleName="Content Review" resetKey={tab}>
          <PermissionGuard user={user} requireModule="contentReview">
            <div style={{
              padding: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <ReviewDashboard user={user} />
            </div>
          </PermissionGuard>
          </ErrorBoundary>
        )}

        {/* Spec Writer Tab */}
        {tab === 'spec-writer' && user && (
          <ErrorBoundary moduleName="Spec Writer" resetKey={tab}>
            <PermissionGuard user={user} requireModule="specWriter">
              <SpecWriter user={user} />
            </PermissionGuard>
          </ErrorBoundary>
        )}

        {/* Strategy Tab */}
        {tab === 'strategy' && (
          <ErrorBoundary moduleName="Strategy" resetKey={tab}>
            <PermissionGuard user={user} requireModule="strategy">
              <StrategyModule user={user} />
            </PermissionGuard>
          </ErrorBoundary>
        )}

        {/* Video Editor Tab (Phase B3) — first AI-ops service. Access is
            enforced backend-side via the video_editor subcategory; no
            frontend module-permission gate exists yet. */}
        {tab === 'video-editor' && (
          <ErrorBoundary moduleName="Video Editor" resetKey={tab}>
            <VideoEditorModule user={user} />
          </ErrorBoundary>
        )}
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // Restore user state from MSAL account OR external token on app load
  useEffect(() => {
    const restoreUserSession = async () => {
      try {
        // First, check for external user token in sessionStorage
        const externalToken = sessionStorage.getItem('externalUserToken');
        if (externalToken) {
          console.log('=== RESTORING EXTERNAL USER SESSION ===');
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
          try {
            // Verify the token is still valid
            const response = await axios.get(`${backendUrl}/api/auth/verify-token`, {
              headers: {
                'Authorization': `Bearer ${externalToken}`
              }
            });

            if (response.data.valid) {
              const userData: User = {
                name: response.data.user.name,
                email: response.data.user.email,
                account: null,
                isExternal: true,
                token: externalToken
              };
              console.log('Restored external user:', userData);
              setUser(userData);
              setIsRestoringSession(false);
              return;
            } else {
              // Token invalid, clear it
              sessionStorage.removeItem('externalUserToken');
            }
          } catch (err) {
            console.error('External token verification failed:', err);
            sessionStorage.removeItem('externalUserToken');
          }
        }

        // Check for MSAL account (internal users)
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const activeAccount = accounts[0];
          console.log('=== RESTORING MSAL USER ===');
          console.log('Active account found:', activeAccount);

          const userData: User = {
            name: activeAccount.name || 'Unknown User',
            email: activeAccount.username || '',
            account: activeAccount,
            isExternal: false
          };

          console.log('Restored user data:', userData);
          setUser(userData);
        } else {
          console.log('No active sessions found, user needs to login');
        }
      } catch (error) {
        console.error('Error restoring user session:', error);
      } finally {
        setIsRestoringSession(false);
      }
    };

    restoreUserSession();
  }, []);

  const handleLoginSuccess = (userData: User) => {
    console.log('=== LOGIN SUCCESS CALLBACK ===');
    console.log('User data received:', userData);
    setUser(userData);
    console.log('User state set:', userData);
  };

  // Show loading while restoring session
  if (isRestoringSession) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  // If external user is logged in, show app directly (bypass MSAL templates)
  if (user?.isExternal) {
    return (
      <MsalProvider instance={msalInstance}>
        <AppContent user={user} setUser={setUser} />
      </MsalProvider>
    );
  }

  // For internal users, use MSAL templates
  return (
    <MsalProvider instance={msalInstance}>
      <UnauthenticatedTemplate>
        <Login onLoginSuccess={handleLoginSuccess} />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AppContent user={user} setUser={setUser} />
      </AuthenticatedTemplate>
    </MsalProvider>
  );
}

export default App;
